import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import { showToast } from '@/helper/toast.helper';
import { AppDispatch, RootState } from '@/store';
import { clearSelectedProject, getProjectById } from '@/store/slices/projectsSlice/projectsSlice';
import { toggleSiteStatus } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { SiteInfo } from '@/types/site.types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Clipboard, Eye, Plus, SendHorizontal, User, UserPlus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AddUserToProjectModal from '../CommonComponents/AddUserToProjectModal';
import ClientSearchFilter, { ClientFilterField } from '../CommonComponents/ClientSearchFilter';
import Loader from '../CommonComponents/Loader';
import ProjectInfoCard from '../CommonComponents/ProjectInfoCard';
import SiteInfoCard from '../CommonComponents/SiteInfoCard';
import StatCard from '../CommonComponents/StatCard';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const userRedIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type FromSource = 'site-management' | 'dashboard';
type NavState = {
  from?: FromSource;
  projectId?: number;
  siteId?: number;
};

const SelectedSiteTracker: React.FC<{
  selectedSite: SiteInfo | null;
  setMarkerPosition: (pos: { x: number; y: number } | null) => void;
}> = ({ selectedSite, setMarkerPosition }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !selectedSite) return;

    const updatePosition = () => {
      const point = map.latLngToContainerPoint([selectedSite.lat, selectedSite.lng]);
      setMarkerPosition({ x: point.x, y: point.y });
    };

    updatePosition();
    map.on('move zoom', updatePosition);

    return () => {
      map.off('move zoom', updatePosition);
    };
  }, [map, selectedSite]);

  return null;
};

const ProjectViewPage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SiteInfo | null>(null);
  const [filteredSites, setFilteredSites] = useState<SiteInfo[]>([]);
  const [filterBy, setFilterBy] = useState<'Sites' | 'Users' | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [markerPosition, setMarkerPosition] = useState<{ x: number; y: number } | null>(null);
  const [openSiteModal, setOpenSiteModal] = useState(false);
  const [selectedSiteDetails, setSelectedSiteDetails] = useState<any>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const {
    selected: project,
    loading,
    selectedProjectStats,
  } = useSelector((state: RootState) => state.projects);

  const navState = (location.state as NavState) || {};
  const projectId = navState.projectId;
  const from = navState.from;

  const [localSites, setLocalSites] = useState<SiteInfo[]>([]);

  const filterFields: ClientFilterField[] = useMemo(() => {
    const fields: ClientFilterField[] = [
      {
        type: 'search',
        key: 'search',
        placeholder: 'Search sites by name or country...',
      },
    ];

    if (isExpanded) {
      fields.push({
        type: 'radio-select',
        key: 'filterBy',
        label: 'filter',
        options: ['Sites', 'Users'],
      });
    }

    return fields;
  }, [isExpanded]);

  const filterSites = (item: SiteInfo, filters: Record<string, any>) => {
    const q = filters.search?.toLowerCase() || '';

    if (filters.filterBy === 'Sites') {
      setFilterBy('Sites');
    } else if (filters.filterBy === 'Users') {
      setFilterBy('Users');
    } else {
      setFilterBy(null);
    }

    return item.name.toLowerCase().includes(q) || item.country.name.toLowerCase().includes(q);
  };

  const handleFilterChange = (filtered: SiteInfo[]) => {
    setFilteredSites(filtered);
  };

  const MarkerClickHandler = ({ site }: { site: SiteInfo }) => {
    const map = useMap();

    return (
      <Marker
        position={[site.lat, site.lng]}
        eventHandlers={{
          click: (e) => {
            const point = map.latLngToContainerPoint(e.latlng);
            setSelectedSite(site);
            setMarkerPosition({ x: point.x, y: point.y });
          },
        }}
      />
    );
  };

  const sitesData = useMemo(() => {
    if (!project?.sites) return [];
    return project.sites.map((site) => ({
      id: site.id,
      name: site.name,
      country: site.country,
      lat: site.geofence.centerLat ?? 0,
      lng: site.geofence.centerLng ?? 0,
      radius: site.geofence.radiusMeters ?? 0,
      projectId: project.id,
      status: site.status,
      languages: site.languages,
      maxUsers: site.maxUsers,
      rewardCurrency: site.currency,
    }));
  }, [project]);

  const usersData = useMemo(() => {
    if (!project?.users) return [];

    return project.users
      .filter(
        (u: any) =>
          u.latitude !== null &&
          u.longitude !== null &&
          !isNaN(Number(u.latitude)) &&
          !isNaN(Number(u.longitude)),
      )
      .map((u: any) => ({
        ...u,
        id: u.id,
        lat: Number(u.latitude),
        lng: Number(u.longitude),
        status: u.status,
        blueNumberId: u.blueNumberId,
      }));
  }, [project]);

  // const usersData = useMemo(() => {
  //   if (!project?.users) return [];

  //   return project.users
  //     .filter(
  //       (u: any) =>
  //         u.latitude !== null &&
  //         u.longitude !== null &&
  //         !isNaN(Number(u.latitude)) &&
  //         !isNaN(Number(u.longitude)),
  //     )
  //     .map((u: any) => ({
  //       ...u,
  //       id: u.id,
  //       lat: Number(u.latitude),
  //       lng: Number(u.longitude),
  //       status: u.status,
  //       blueNumberId: u.blueNumberId,
  //     }));
  // }, [project]);

  // const usersData = useMemo(() => {
  //   if (!project?.users) return [];
  //   return project.users.map((u: any) => ({
  //     ...u,
  //     lat: Number(u.latitude),
  //     lng: Number(u.longitude),
  //   }));
  // }, [project]);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const ResizeMap = ({ isExpanded }: { isExpanded: boolean }) => {
    const map = useMap();
    useEffect(() => {
      setTimeout(() => map.invalidateSize(), 300);
    }, [isExpanded, map]);
    return null;
  };

  const handleMarkerClick = (site: any) => {
    setSelectedSite(site);
  };

  const mapCenter = useMemo<[number, number]>(() => {
    if (filteredSites.length === 0) return [20.5937, 78.9629];

    const avgLat = filteredSites.reduce((sum, site) => sum + site.lat, 0) / filteredSites.length;
    const avgLng = filteredSites.reduce((sum, site) => sum + site.lng, 0) / filteredSites.length;

    return [avgLat, avgLng];
  }, [filteredSites]);

  const statsCards = [
    {
      id: 1,
      title: 'Total Users',
      value: selectedProjectStats?.totalusers || '0',
      icon: User,
      iconBg: 'bg-green-200',
    },
    {
      id: 2,
      title: 'Total Survey Sets',
      value: selectedProjectStats?.totalSurveySets || '0',
      icon: Clipboard,
      iconBg: 'bg-blue-200',
    },
    {
      id: 3,
      title: 'Active Surveys',
      value: '-',
      icon: Clipboard,
      iconBg: 'bg-pink-200',
    },
    {
      id: 4,
      title: 'Total Notifications',
      value: selectedProjectStats?.notification || '-',
      icon: Clipboard,
      iconBg: 'bg-green-100',
    },
  ];

  const handleEditSiteNavigation = (site: SiteInfo) => {
    navigate('/dashboard/site-management/EditSite', {
      state: {
        siteId: site.id,
        projectId: project?.id,
        from: 'dashboard',
      },
    });
  };

  const handleViewSite = (site: any) => {
    setSelectedSite(site);
    setOpenSiteModal(true);
  };

  const handleToggleStatus = async (site: SiteInfo) => {
    const newStatus = !site.status;

    setLocalSites((prev) => prev.map((s) => (s.id === site.id ? { ...s, status: newStatus } : s)));

    try {
      await dispatch(
        toggleSiteStatus({
          id: site.id,
          status: newStatus,
        }),
      ).unwrap();

      showToast.success(
        newStatus ? 'Site unarchived successfully.' : 'Site archived successfully.',
      );
    } catch (error: any) {
      setLocalSites((prev) =>
        prev.map((s) => (s.id === site.id ? { ...s, status: site.status } : s)),
      );

      showToast.error(error?.message || 'Failed to update site status');
    }
  };

  const getSiteDetailFields = (site: any) => {
    if (!site) return [];

    return [
      { label: 'Site Name', value: site.name || 'N/A', half: true },

      { label: 'Country', value: site.country?.name || 'N/A', half: true },

      {
        label: 'Longitude',
        value: site.geofence?.centerLng !== undefined ? site.geofence.centerLng : 'N/A',
        half: true,
      },

      {
        label: 'Latitude',
        value: site.geofence?.centerLat !== undefined ? site.geofence.centerLat : 'N/A',
        half: true,
      },

      {
        label: 'Radius',
        value:
          site.geofence?.radiusMeters !== undefined
            ? `${(site.geofence.radiusMeters / 1000).toFixed(2)} km`
            : 'N/A',
        half: true,
      },

      { label: 'Max Users', value: site.maxUsers ?? 'N/A', half: true },

      {
        label: 'Language(s)',
        value:
          Array.isArray(site.languages) && site.languages.length > 0
            ? site.languages.map((l: any) => l.name).join(', ')
            : 'N/A',
        colSpan: 2,
      },

      { label: 'Reward Currency', value: site.currency || 'N/A', half: true },

      { label: 'Project', value: project?.name || 'N/A', half: true },

      {
        label: 'Status',
        value: typeof site.status === 'boolean' ? (site.status ? 'Active' : 'Inactive') : 'N/A',
        badge: true,
        colSpan: 2,
      },
    ];
  };

  useEffect(() => {
    setLocalSites(sitesData);
  }, [sitesData]);

  useEffect(() => {
    setFilteredSites(sitesData);
    setFilteredUsers(usersData);
  }, [sitesData]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectById(projectId.toString()));
    }

    return () => {
      dispatch(clearSelectedProject());
    };
  }, [projectId, dispatch]);

  useEffect(() => {
    if (project && navState.siteId) {
      const siteToSelect = sitesData.find((s) => s.id === navState.siteId);
      if (siteToSelect) {
        setSelectedSite(siteToSelect);
        setIsExpanded(true);
      }
    }
  }, [project, sitesData, navState.siteId]);

  if (loading) {
    return (
      <PageHeaderWrapper
        title="Site Management"
        description="View and monitor all your active sites."
        contentClassName="p-6 space-y-6 mt-[100px]"
        onAdd={() => navigate(-1)}
        addLabel={from === 'site-management' ? 'Return to sites dashboard' : 'Return to Project'}
      >
        <Loader />
      </PageHeaderWrapper>
    );
  }

  if (!project && !loading) {
    return (
      <PageHeaderWrapper
        title="Site Management"
        description="View and monitor all your active sites."
        contentClassName="p-6 space-y-6 mt-[100px]"
        onAdd={() => navigate(-1)}
        addLabel={from === 'site-management' ? 'Return to sites dashboard' : 'Return to Project'}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 font-semibold">Failed to load project data</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-[#621C43] text-white rounded-lg hover:bg-[#4A082D]"
            >
              Go Back
            </button>
          </div>
        </div>
      </PageHeaderWrapper>
    );
  }

  return (
    <PageHeaderWrapper
      title="Site Management"
      description="View and monitor all your active sites."
      onAdd={() => navigate(-1)}
      addLabel={from === 'site-management' ? 'Return to sites dashboard' : 'Return to Project'}
    >
      {project && (
        <ProjectInfoCard
          project={project}
          showEdit={false}
          showExpand={false}
          showFile={false}
          showCollapse
          onCollapse={() => navigate(-1)}
          enableCollapse={true}
        />
      )}

      <ClientSearchFilter<SiteInfo>
        data={localSites}
        fields={filterFields}
        customFilterFn={filterSites}
        onFilter={setFilteredSites}
      />

      {/* Summary Cards */}
      {!isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {statsCards.map((stat) => (
            <StatCard
              key={stat.id}
              icon={stat.icon}
              iconBg={stat.iconBg}
              title={stat.title}
              value={stat.value}
            />
          ))}
        </div>
      )}

      {/* Map & Quick Actions */}
      <div
        className={`flex flex-col lg:flex-row gap-4 transition-all duration-500 ${
          isExpanded ? 'items-start' : ''
        }`}
      >
        {/* Site Map */}
        <div
          className={`${
            isExpanded ? 'w-full' : 'lg:w-2/4'
          } bg-[#F2EBEE] rounded-lg p-3 relative transition-all duration-500`}
        >
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-bold text-xl">Site Map</h2>
          </div>

          {/* Map Container */}
          <div
            className={`relative ${
              isExpanded ? 'h-[70vh]' : 'h-64'
            } w-full rounded-3xl shadow-lg overflow-hidden transition-all duration-500`}
          >
            <MapContainer
              maxBounds={[
                [-90, -180],
                [90, 180],
              ]}
              maxBoundsViscosity={1.0}
              center={mapCenter}
              zoom={3}
              className="h-full w-full z-0"
            >
              <TileLayer noWrap={true} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              <ResizeMap isExpanded={isExpanded} />
              <SelectedSiteTracker
                selectedSite={selectedSite}
                setMarkerPosition={setMarkerPosition}
              />

              {filterBy !== 'Users' &&
                filteredSites &&
                filteredSites.length > 0 &&
                filteredSites.map((site, idx) => {
                  if (site.lat && site.lng)
                    return (
                      <React.Fragment key={idx}>
                        {site.radius && (
                          <Circle
                            center={[site.lat, site.lng]}
                            radius={site.radius}
                            pathOptions={{
                              fillColor: '#621C43',
                              fillOpacity: 0.2,
                              color: '#621C43',
                              weight: 2,
                            }}
                          />
                        )}
                        <MarkerClickHandler site={site} />
                      </React.Fragment>
                    );
                  return null;
                })}

              {filterBy !== 'Sites' &&
                usersData &&
                usersData.length > 0 &&
                usersData.map((user: any) => {
                  const lat = Number(user.lat);
                  const lng = Number(user.lng);
                  if (isNaN(lat) || isNaN(lng)) return null;
                  return (
                    <Marker
                      key={`user-${user.id}`}
                      position={[lat, lng]}
                      icon={
                        new L.Icon({
                          iconUrl:
                            'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
                          shadowUrl:
                            'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41],
                        })
                      }
                    >
                      <Popup>
                        <div className="text-sm">
                          <p className="font-semibold">User ID: {user.userId}</p>
                          <p>Status: {user.status ? 'Active' : 'Inactive'}</p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}

              {filterBy === 'Users' &&
                filteredUsers &&
                filteredUsers.length > 0 &&
                filteredUsers.map((user: any) => {
                  const lat = Number(user.lat);
                  const lng = Number(user.lng);

                  if (isNaN(lat) || isNaN(lng)) return null;

                  return (
                    <Marker key={`user-${user.id}`} position={[lat, lng]} icon={userRedIcon}>
                      <Popup>
                        <div className="text-sm">
                          <p className="font-semibold">User</p>
                          <p>ID: {user.blueNumberId}</p>
                          <p>Status: {user.status ? 'Active' : 'Inactive'}</p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
            </MapContainer>

            {/* Expand / Compress Button */}
            <button
              onClick={toggleExpand}
              className="absolute bottom-3 right-3 p-3 rounded-md text-gray-600 transition z-[10]"
            >
              <img
                src="/expand.png"
                alt="Expand Map"
                className={`w-10 h-10 transform transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Info Card Inside Map (Visible Only When Not Expanded) */}
            {isExpanded && selectedSite && markerPosition && (
              <div
                className="absolute bg-[#AD0C62] bg-opacity-20 border border-[#621C43] shadow-lg rounded-lg p-3 flex justify-between items-center w-[180px] z-[999]"
                style={{
                  left: markerPosition.x + 15,
                  top: markerPosition.y - 30,
                }}
              >
                {/* Left */}
                <div>
                  <h3 className="font-semibold text-sm capitalize">{selectedSite.name}</h3>
                  <p className="text-gray-500 text-xs capitalize">{selectedSite.country.name}</p>
                </div>

                {/* Actions */}
                <div className="flex gap-1">
                  <button
                    className="p-1 bg-[#AD0C62] bg-opacity-20 rounded-md hover:bg-[#EDAAC5]"
                    onClick={() => alert(`More info about ${selectedSite.name}`)}
                  >
                    <img src="/info.png" alt="Info" className="w-4 h-4" />
                  </button>

                  <button
                    className="p-1 bg-[#AD0C62] bg-opacity-20 rounded-md hover:bg-[#EDAAC5]"
                    onClick={() =>
                      navigate(`/dashboard/site-management/EditSite`, {
                        state: { siteId: selectedSite.id, project },
                      })
                    }
                  >
                    <img src="/right-arrow.png" alt="Right-arrow" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions (hide when expanded) */}
        {!isExpanded && (
          <div className="lg:w-2/4 bg-[#F2EBEE] rounded-lg p-3 transition-all duration-300 flex flex-col">
            <h2 className="font-bold text-xl mb-3">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1 auto-rows-fr gap-x-2">
              <button
                onClick={() => navigate('/dashboard/surveyset-management/create')}
                className="bg-[#EDAAC5] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Plus size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">Create Survey(s) Set</p>
              </button>
              <button
                className="bg-blue-200 rounded-xl flex flex-col items-center gap-1 justify-center w-full"
                onClick={() => setIsAddUserOpen(true)}
              >
                <UserPlus size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">Add User(s)</p>
              </button>
              <button
                onClick={() => navigate('/dashboard/notification-management/create')}
                className="bg-[#F3C68D] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <SendHorizontal size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">Send Notification(s)</p>
              </button>
              <button
                onClick={() => navigate('/dashboard/surveyset-management')}
                className="bg-[#F0D1DD] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Eye size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">View Survey(s) Set</p>
              </button>
              <button
                onClick={() => navigate('/dashboard/project-management/user-management')}
                className="bg-[#D1D7EC] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Eye size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">View User(s)</p>
              </button>
              <button
                onClick={() => navigate('/dashboard/notification-management')}
                className="bg-[#F3DCC7] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Eye size={35} color="#000" strokeWidth={1} />
                <span className="mt-2 text-sm text-center">View Notifications</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Site Cards (hide when expanded) */}
      {!isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          {filteredSites.map((site, idx) => (
            <SiteInfoCard
              key={idx}
              site={site}
              onView={handleViewSite}
              onToggleStatus={() => handleToggleStatus(site)}
              handleArrowClick={() => handleEditSiteNavigation(site)}
            />
          ))}
          {filteredSites.length > 4 && (
            <button className="bg-gray-300 p-4 rounded-lg shadow hover:bg-gray-400 transition">
              See all ({filteredSites.length - 4} sites)
            </button>
          )}
        </div>
      )}
      {project && (
        <AddUserToProjectModal
          projectId={project.id}
          isOpen={isAddUserOpen}
          onClose={() => setIsAddUserOpen(false)}
        />
      )}

      <ViewDetailsModal
        isOpen={openSiteModal}
        onClose={() => setOpenSiteModal(false)}
        title="Site Details"
        data={selectedSite || {}}
        fields={getSiteDetailFields(selectedSite)}
      />
    </PageHeaderWrapper>
  );
};

export default ProjectViewPage;
